import AppendRoot from '@component/appendRoot'
import { CheckboxTheme } from '@component/form/checkbox/content'
import { useMapboxContext } from '@component/mapbox/map/context'
import { global } from '@store/index'
import { stopPropagation } from '@utils'
import classNames from 'classnames'
import { isFunction } from 'lodash-es'
import { useCallback, useEffect } from 'react'
import { useBoolean, useLocalStorage } from 'react-use'
import { useRecoilValue } from 'recoil'

export function SearchTypeMenuContent({
  showSale,
  showRent,
  showSold,
  showEstimate,
  toggleSale,
  toggleRent,
  toggleSold,
  toggleEstimate,
  saleText = '',
  rentText = '',
  soldText = '',
  estimateText = '',
  TypeItem
}) {
  const { isMobile } = useRecoilValue(global)
  const [visible, toggleVisible] = useBoolean(false)
  const [showTip, setShowTip] = useLocalStorage('new-search-type-tip', true)
  const { map, mapLoaded } = useMapboxContext()

  function handleToggleVisible() {
    toggleVisible()
    setShowTip(false)
  }

  useEffect(() => {
    if (!mapLoaded) return

    function handleClick() {
      toggleVisible(false)
    }
    map.on('click', handleClick)

    return () => {
      map.off('click', handleClick)
    }
  }, [mapLoaded, map, toggleVisible])

  return (
    <div className='relative'>
      <div
        className='w-40 h-40 rounded-sm cursor-pointer relative bg-white grid place-items-center shadow-md hover:bg-neutral-2'
        onClick={handleToggleVisible}
      >
        <div className='grid grid-cols-2 gap-4'>
          <i className='w-8 h-8 rounded-half bg-orange' />
          <i className='w-8 h-8 rounded-half bg-purple' />
          <i className='w-8 h-8 rounded-half bg-red' />
          <i className='w-8 h-8 rounded-half bg-estimate' />
        </div>
      </div>
      {
        showTip && (
          <div className='absolute right-64 -top-30 bg-white px-14 pt-14 pb-10 rounded shadow-md w-250'>
            <span className='text-primary bg-primary-light text-sm font-medium py-4 px-11 rounded-full'>NEW</span>
            <button className='absolute right-0 top-0 p-10 hover:!bg-transparent' onClick={() => setShowTip(false)}>
              <i className='icon icon-close font-bold !text-base'></i>
            </button>
            <p className='mt-10 text-base'>Filter and explore properties by Sale, Estimate, Rent and Sold.</p>
            <i className='w-0 h-0 block absolute left-full top-36 border-[12px] border-transparent border-l-white' />
          </div>
        )
      }
      {
        isMobile
          ? visible && (
            <TypePopupMobile
              showSale={showSale}
              showRent={showRent}
              showSold={showSold}
              showEstimate={showEstimate}
              toggleSale={toggleSale}
              toggleRent={toggleRent}
              toggleSold={toggleSold}
              toggleEstimate={toggleEstimate}
              saleText={saleText}
              rentText={rentText}
              soldText={soldText}
              estimateText={estimateText}
              TypeItem={TypeItem}
              visible
              toggleVisible={toggleVisible}
            />
          )
          : (
            <TypePopupDesktop
              showSale={showSale}
              showRent={showRent}
              showSold={showSold}
              showEstimate={showEstimate}
              toggleSale={toggleSale}
              toggleRent={toggleRent}
              toggleSold={toggleSold}
              toggleEstimate={toggleEstimate}
              saleText={saleText}
              rentText={rentText}
              soldText={soldText}
              estimateText={estimateText}
              TypeItem={TypeItem}
              visible={visible}
            />
          )
      }
    </div>
  )
}

function TypePopupMobile({
  visible,
  toggleVisible,
  showSale,
  showRent,
  showSold,
  showEstimate,
  toggleSale,
  toggleRent,
  toggleSold,
  toggleEstimate,
  saleText,
  rentText,
  soldText,
  estimateText,
  TypeItem
}) {

  const [localShowSale, setLocalShowSale] = useBoolean(showSale)
  const [localShowRent, setLocalShowRent] = useBoolean(showRent)
  const [localShowSold, setLocalShowSold] = useBoolean(showSold)
  const [localShowEstimate, setLocalShowEstimate] = useBoolean(showEstimate)

  // 全部都没有选中
  const allUnchecked = !localShowSale && !localShowRent && !localShowSold && !localShowEstimate

  useEffect(() => {
    const mapboxCtrlTopRightEl = document.querySelector('.mapboxgl-ctrl-top-right')
    if (mapboxCtrlTopRightEl) {
      mapboxCtrlTopRightEl.style.zIndex = visible ? 'initial' : 2
    }
  }, [visible])

  const handleApply = useCallback(() => {
    isFunction(toggleSale) && toggleSale(localShowSale)
    isFunction(toggleRent) && toggleRent(localShowRent)
    isFunction(toggleSold) && toggleSold(localShowSold)
    isFunction(toggleEstimate) && toggleEstimate(localShowEstimate)
    toggleVisible(false)
  }, [
    localShowSale,
    localShowRent,
    localShowSold,
    localShowEstimate,
    toggleVisible,
    toggleSale,
    toggleRent,
    toggleSold,
    toggleEstimate
  ])

  return (
    <AppendRoot>
      <div
        className={classNames(
          'fixed inset-0 bg-black bg-opacity-50 flex flex-col justify-end z-[110] transition-opacity touch-none',
          visible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={toggleVisible}
      >
        <div className='bg-white pt-38 pb-24 px-18 rounded-t-2xl relative' onClick={stopPropagation}>
          <i className='w-40 h-6 rounded-full bg-[#D9D9D9] absolute left-1/2 top-10 -translate-x-1/2' />
          <div className='text-lg font-medium'>Property types</div>
          <div className='space-y-18 mt-26'>
            {
              saleText && (
                <TypeItem visible={visible} value={localShowSale} onChange={setLocalShowSale} theme={CheckboxTheme.secondary} text={saleText}></TypeItem>
              )
            }
            {
              rentText && (
                <TypeItem visible={visible} value={localShowRent} onChange={setLocalShowRent} theme={CheckboxTheme.purple} text={rentText}></TypeItem>
              )
            }
            {
              soldText && (
                <TypeItem visible={visible} value={localShowSold} onChange={setLocalShowSold} theme={CheckboxTheme.red} text={soldText}></TypeItem>
              )
            }
            {
              estimateText && (
                <TypeItem visible={visible} value={localShowEstimate} onChange={setLocalShowEstimate} theme={CheckboxTheme.green} text={estimateText}></TypeItem>
              )
            }
          </div>
          <button type='button' className='button-orange w-full mt-24' disabled={allUnchecked} onClick={handleApply}>Apply</button>
        </div>
      </div>
    </AppendRoot>
  )
}

function TypePopupDesktop({
  visible,
  showSale,
  showRent,
  showSold,
  showEstimate,
  toggleSale,
  toggleRent,
  toggleSold,
  toggleEstimate,
  saleText,
  rentText,
  soldText,
  estimateText,
  TypeItem
}) {

  return (
    <div className={classNames(
      'bg-white rounded transition-opacity absolute top-full right-0 mt-10 hidden sm:block',
      visible ? 'opacity-100 p-14' : 'opacity-0 w-0 h-0 overflow-hidden pointer-events-none'
    )}>
      <div className='whitespace-nowrap text-base font-medium'>Property types</div>
      <div className='space-y-16 mt-24'>
        {
          saleText && (
            <TypeItem visible={visible} value={showSale} onChange={toggleSale} theme={CheckboxTheme.secondary} text={saleText}></TypeItem>
          )
        }
        {
          rentText && (
            <TypeItem visible={visible} value={showRent} onChange={toggleRent} theme={CheckboxTheme.purple} text={rentText}></TypeItem>
          )
        }
        {
          soldText && (
            <TypeItem visible={visible} value={showSold} onChange={toggleSold} theme={CheckboxTheme.red} text={soldText}></TypeItem>
          )
        }
        {
          estimateText && (
            <TypeItem visible={visible} value={showEstimate} onChange={toggleEstimate} theme={CheckboxTheme.green} text={estimateText}></TypeItem>
          )
        }
      </div>
    </div>
  )
}
